import { forwardRef, useImperativeHandle, useState } from 'react';
import { Input } from '@/components/ui/input';
import TabErrorBanner from '@/components/v2/molecules/admin/shared/TabErrorBanner';
import GuestsEmptyBanner from '@/components/v2/molecules/admin/tabs/AdminGuestsTab/GuestsEmptyBanner';
import GuestsFilteredBanner from '@/components/v2/molecules/admin/tabs/AdminGuestsTab/GuestsFilteredBanner';
import GuestsTabSkeleton from '@/components/v2/molecules/admin/tabs/AdminGuestsTab/GuestsTabSkeleton';
import GuestFormModal from '@/components/v2/organisms/admin/tabs/AdminGuestsTab/GuestFormModal';
import useGuestModal from '@/hooks/admin/AdminGuestsTab/useGuestsModal';
import useRolePermissions from '@/hooks/admin/shared/useRolePermissions';
import { useGuests } from '@/hooks/useGuests';
import { Search } from 'lucide-react';
import GuestsTable from '../AdminGuestsTab/GuestsTable';

export interface ReservationsGuestsViewRef {
  createNew: () => void;
  hasData: boolean;
}

const ReservationsGuestsView = forwardRef<ReservationsGuestsViewRef, object>(
  (_, ref) => {
    const { canEdit, canDelete, role, userId } = useRolePermissions();

    const {
      data: guests = [],
      isLoading: isLoadingGuests,
      error: errorGuests,
      refetch: refetchGuests,
    } = useGuests({ role, userId });

    const [searchTerm, setSearchTerm] = useState('');

    const {
      isModalOpen,
      guestToEdit,
      openCreateModal,
      openEditModal,
      closeModal,
      handleDeleteGuest,
    } = useGuestModal();

    const filteredGuests = guests.filter((guest) => {
      if (!searchTerm) return true;

      const searchLower = searchTerm.toLowerCase();
      const nameMatch = guest.name.toLowerCase().includes(searchLower);
      const emailMatch =
        guest.email?.toLowerCase().includes(searchLower) || false;

      const originMatch =
        guest.origins?.name.toLowerCase().includes(searchLower) || false;

      return nameMatch || emailMatch || originMatch;
    });

    useImperativeHandle(ref, () => ({
      createNew: openCreateModal,
      hasData: guests.length > 0,
    }));

    if (isLoadingGuests) {
      return <GuestsTabSkeleton />;
    }

    if (errorGuests) {
      return (
        <TabErrorBanner
          title="Error al cargar huéspedes"
          message="No se pudieron cargar los huéspedes. Por favor, intenta nuevamente."
          onRetry={() => refetchGuests()}
        />
      );
    }

    return (
      <>
        <div className="space-y-6">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, correo u origen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-[300px] rounded-full bg-muted/50 hover:bg-muted transition-colors border-0 h-10"
            />
          </div>

          {guests.length === 0 ? (
            <GuestsEmptyBanner onCreate={openCreateModal} />
          ) : filteredGuests.length === 0 ? (
            <GuestsFilteredBanner
              searchTerm={searchTerm}
              onClear={() => setSearchTerm('')}
            />
          ) : (
            <GuestsTable
              guests={filteredGuests}
              onEdit={openEditModal}
              onDelete={handleDeleteGuest}
              canEdit={canEdit}
              canDelete={canDelete}
            />
          )}
        </div>

        <GuestFormModal
          isOpen={isModalOpen}
          onClose={closeModal}
          guest={guestToEdit}
        />
      </>
    );
  }
);

ReservationsGuestsView.displayName = 'ReservationsGuestsView';

export default ReservationsGuestsView;
